/**
 * Разница двух редакций карточки по словам: удалённое — красным и зачёркнутым, добавленное — зелёным.
 * Цвет дублируется знаком (−/+) для тех, кто цвет не различает.
 */

type Part = { t: "same" | "add" | "del"; s: string };

function слова(s: string): string[] {
  return s.split(/(\s+)/).filter((x) => x.length);
}

/** LCS по словам; для текстов карточек (сотни слов) быстро достаточно. */
export function diff(a: string, b: string): Part[] {
  const x = слова(a);
  const y = слова(b);
  const n = x.length;
  const m = y.length;
  const w = m + 1;
  const dp = new Int32Array((n + 1) * w);
  const at = (i: number, j: number) => dp[i * w + j] ?? 0;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * w + j] = x[i] === y[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const out: Part[] = [];
  const push = (t: Part["t"], s: string) => {
    const last = out[out.length - 1];
    if (last && last.t === t) last.s += s;
    else out.push({ t, s });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const xi = x[i] ?? "";
    const yj = y[j] ?? "";
    if (xi === yj) {
      push("same", xi);
      i++;
      j++;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      push("del", xi);
      i++;
    } else {
      push("add", yj);
      j++;
    }
  }
  for (; i < n; i++) push("del", x[i] ?? "");
  for (; j < m; j++) push("add", y[j] ?? "");
  return out;
}

export function ContentDiff({ before, after, label }: { before: string; after: string; label?: string }) {
  const parts = diff(before, after);
  const changed = parts.some((p) => p.t !== "same");
  return (
    <div className="text-[13px] leading-relaxed">
      {label && <div className="mb-1 text-[11px] uppercase tracking-wide text-fg-faint">{label}</div>}
      {!changed ? (
        <div className="text-fg-muted">без изменений</div>
      ) : (
        <div className="whitespace-pre-wrap break-words">
          {parts.map((p, i) =>
            p.t === "same" ? (
              <span key={i}>{p.s}</span>
            ) : p.t === "add" ? (
              <span key={i} className="rounded-sm bg-ok/15 text-ok" title="добавлено">
                {p.s.trim() ? "+" : ""}
                {p.s}
              </span>
            ) : (
              <span key={i} className="rounded-sm bg-err/15 text-err line-through" title="удалено">
                {p.s.trim() ? "−" : ""}
                {p.s}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}
